import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  fetchApiGet,
  fetchApiPut,
  fetchApiPost,
  fetchApiDelete,
} from "../utils";
import { BaseDbObject } from "@/lib/models/db";
import { zodStrictRequiredDecimal, zodOptionalDecimal } from "../common";

// Schemas
export const CreateMarketZoneBreakdownSchema = z.object({
  customer_id: z.string().uuid("Please select a customer"),
  market_zone_id: z.string().uuid("Please select a market zone"),
  commodity: z.string().min(1, "Commodity is required"),
  crop_year: z.number().int().positive("Crop year must be a positive integer"),
  month_one: zodOptionalDecimal({ key: "month_one" }),
  month_two: zodOptionalDecimal({ key: "month_two" }),
  month_three: zodOptionalDecimal({ key: "month_three" }),
  month_four: zodOptionalDecimal({ key: "month_four" }),
  month_five: zodOptionalDecimal({ key: "month_five" }),
  month_six: zodOptionalDecimal({ key: "month_six" }),
  month_seven: zodOptionalDecimal({ key: "month_seven" }),
  month_eight: zodOptionalDecimal({ key: "month_eight" }),
  month_nine: zodOptionalDecimal({ key: "month_nine" }),
  month_ten: zodOptionalDecimal({ key: "month_ten" }),
  month_eleven: zodOptionalDecimal({ key: "month_eleven" }),
  month_twelve: zodOptionalDecimal({ key: "month_twelve" }),
  total: zodStrictRequiredDecimal({ key: "total" }),
  amount: zodStrictRequiredDecimal({ key: "amount" }),
  note: z.string().optional()
});

export const UpdateMarketZoneBreakdownSchema = z.object({
  customer_id: z.string().uuid().optional(),
  market_zone_id: z.string().uuid().optional(),
  commodity: z.string().optional(),
  crop_year: z.number().int().positive().optional(),
  month_one: zodOptionalDecimal({ key: "month_one" }),
  month_two: zodOptionalDecimal({ key: "month_two" }),
  month_three: zodOptionalDecimal({ key: "month_three" }),
  month_four: zodOptionalDecimal({ key: "month_four" }),
  month_five: zodOptionalDecimal({ key: "month_five" }),
  month_six: zodOptionalDecimal({ key: "month_six" }),
  month_seven: zodOptionalDecimal({ key: "month_seven" }),
  month_eight: zodOptionalDecimal({ key: "month_eight" }),
  month_nine: zodOptionalDecimal({ key: "month_nine" }),
  month_ten: zodOptionalDecimal({ key: "month_ten" }),
  month_eleven: zodOptionalDecimal({ key: "month_eleven" }),
  month_twelve: zodOptionalDecimal({ key: "month_twelve" }),
  total: zodOptionalDecimal({ key: "total" }),
  amount: zodOptionalDecimal({ key: "amount" }),
  note: z.string().optional().nullable()
});

export const ListMarketZoneBreakdownsQuerySchema = z.object({
  customer_id: z.string().uuid().optional(),
  market_zone_id: z.string().uuid().optional(),
  commodity: z.string().optional(),
  crop_year: z.coerce.number().int().positive().optional(),
  page: z.coerce.number().min(1).default(1).optional(),
  limit: z.coerce.number().min(1).max(100).default(50).optional()
});

// Types
export interface MarketZoneBreakdown extends BaseDbObject {
  customer_id: string;
  market_zone_id: string;
  commodity: string;
  crop_year: number;
  month_one?: string;
  month_two?: string;
  month_three?: string;
  month_four?: string;
  month_five?: string;
  month_six?: string;
  month_seven?: string;
  month_eight?: string;
  month_nine?: string;
  month_ten?: string;
  month_eleven?: string;
  month_twelve?: string;
  month_one_label?: string;
  month_two_label?: string;
  month_three_label?: string;
  month_four_label?: string;
  month_five_label?: string;
  month_six_label?: string;
  month_seven_label?: string;
  month_eight_label?: string;
  month_nine_label?: string;
  month_ten_label?: string;
  month_eleven_label?: string;
  month_twelve_label?: string;
  total: string;
  amount: string;
  note?: string;
}

export interface ListMarketZoneBreakdownsResult {
  data: MarketZoneBreakdown[];
  total: number;
  page: number;
  limit: number;
}

// Hooks
export function useCreateMarketZoneBreakdown() {
  const queryClient = useQueryClient();

  return async (data: z.infer<typeof CreateMarketZoneBreakdownSchema>) => {
    const result = await fetchApiPost<MarketZoneBreakdown>(
      "risk/market-zone-breakdowns",
      data
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk", "market-zone-breakdowns"] });
    }

    return result;
  };
}

export function useListMarketZoneBreakdowns(query?: z.infer<typeof ListMarketZoneBreakdownsQuerySchema>) {
  const queryParams = new URLSearchParams();

  if (query) {
    if (query.customer_id) {
      queryParams.set("customer_id", query.customer_id);
    }
    if (query.market_zone_id) {
      queryParams.set("market_zone_id", query.market_zone_id);
    }
    if (query.commodity) {
      queryParams.set("commodity", query.commodity);
    }
    if (query.crop_year !== undefined) {
      queryParams.set("crop_year", query.crop_year.toString());
    }
    if (query.page) {
      queryParams.set("page", query.page.toString());
    }
    if (query.limit) {
      queryParams.set("limit", query.limit.toString());
    }
  }

  return useQuery({
    queryKey: ["risk", "market-zone-breakdowns", queryParams.toString()],
    queryFn: () =>
      fetchApiGet<ListMarketZoneBreakdownsResult>("risk/market-zone-breakdowns", queryParams),
    retry: 2,
    enabled: !!query?.crop_year && !!query.commodity,
    keepPreviousData: true,
  });
}

export function useGetMarketZoneBreakdown(id: string) {
  return useQuery({
    queryKey: ["risk", "market-zone-breakdowns", id],
    queryFn: () =>
      fetchApiGet<MarketZoneBreakdown>(`risk/market-zone-breakdowns/${id}`),
    enabled: !!id,
    retry: 2,
  });
}

export function useUpdateMarketZoneBreakdown() {
  const queryClient = useQueryClient();

  return async (id: string, data: z.infer<typeof UpdateMarketZoneBreakdownSchema>) => {
    const result = await fetchApiPut<MarketZoneBreakdown>(
      `risk/market-zone-breakdowns/${id}`,
      data
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk", "market-zone-breakdowns"] });
    }

    return result;
  };
}

export function useDeleteMarketZoneBreakdown() {
  const queryClient = useQueryClient();

  return async (id: string) => {
    const result = await fetchApiDelete<{}>(
      `risk/market-zone-breakdowns/${id}`,
      new URLSearchParams()
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk", "market-zone-breakdowns"] });
    }

    return result;
  };
}
